import type { ArtistAvailabilityAdjustment, ArtistWeeklyFinanceSummary, FinanceDataReliability, Person, Task, TimeLog } from "../models/production";

export const DEFAULT_MINUTES_PER_DAY = 480;
const DEFAULT_WORKING_DAYS = [1, 2, 3, 4, 5];
const COMPLIANCE_TARGET_RATIO = 0.9;
const ALLOCATION_TARGET_RATIO = 0.9;

export interface ArtistWeeklyFinanceContext {
  tasks?: Task[];
  timeLogs?: TimeLog[];
  availabilityAdjustments?: ArtistAvailabilityAdjustment[];
  now?: string;
  minutesPerDay?: number;
}

function startOfIsoWeek(dateLike: string | Date) {
  const date = new Date(dateLike);
  const utcDate = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
  const day = utcDate.getUTCDay() || 7;
  utcDate.setUTCDate(utcDate.getUTCDate() - day + 1);
  return utcDate;
}

function addUtcDays(date: Date, days: number) {
  const next = new Date(date.getTime());
  next.setUTCDate(next.getUTCDate() + days);
  return next;
}

function toIsoDate(date: Date) {
  return date.toISOString().slice(0, 10);
}

function isWorkingDay(date: Date) {
  const day = date.getUTCDay();
  return DEFAULT_WORKING_DAYS.includes(day === 0 ? 7 : day);
}

function eachWorkingDayBetween(start: Date, end: Date) {
  const days: string[] = [];
  let cursor = new Date(start.getTime());
  while (cursor <= end) {
    if (isWorkingDay(cursor)) {
      days.push(toIsoDate(cursor));
    }
    cursor = addUtcDays(cursor, 1);
  }
  return days;
}

function countWorkingDaysBetween(startIso?: string, endIso?: string) {
  if (!startIso || !endIso) return 0;
  const start = startOfIsoWeek(startIso);
  const end = new Date(endIso);
  end.setUTCHours(0, 0, 0, 0);
  if (end < start) return 0;
  return eachWorkingDayBetween(start, end).length;
}

function clamp(value: number, min: number, max: number) {
  return Math.max(min, Math.min(max, value));
}

function roundDays(value: number) {
  return Math.round(value * 100) / 100;
}

function scoreToConfidence(score: number): FinanceDataReliability {
  if (score >= 80) return "high";
  if (score >= 55) return "medium";
  return "low";
}

function buildStatusSummary(status: ArtistWeeklyFinanceSummary["utilisationStatus"]) {
  switch (status) {
    case "compliant_and_fully_allocated":
      return {
        summaryLabel: "Compliant and fully allocated",
        helperText: "Timelogs are healthy and the artist has a near-full week of allocated work.",
      };
    case "compliant_but_under_allocated":
      return {
        summaryLabel: "Compliant but under-allocated",
        helperText: "Timelog compliance looks good, but the artist does not appear to have a full week of allocated work.",
      };
    case "under_logging":
      return {
        summaryLabel: "Under-logging",
        helperText: "Logged effort is materially below the expected working week, so managers should follow up on timelog compliance.",
      };
    case "over_logging_vs_allocation":
      return {
        summaryLabel: "Over-logging vs allocation",
        helperText: "The artist has logged materially more time than was allocated, which can indicate overruns, misallocation, or logging issues.",
      };
    case "unavailable_approved_leave":
      return {
        summaryLabel: "Unavailable due to approved leave",
        helperText: "Availability adjustments reduce the expected working week, so utilisation should be interpreted against the reduced capacity.",
      };
    case "missing_data_uncertain":
    default:
      return {
        summaryLabel: "Missing data / uncertain",
        helperText: "Scheduling or logging inputs are incomplete, so this weekly summary should be treated cautiously.",
      };
  }
}

function getWeekRange(weekStartIso: string) {
  const start = startOfIsoWeek(weekStartIso);
  const end = addUtcDays(start, 6);
  return {
    weekStart: toIsoDate(start),
    weekEnd: toIsoDate(end),
    workingDates: eachWorkingDayBetween(start, end),
  };
}

function getAvailabilityForWeek(personId: string, weekStart: string, adjustments: ArtistAvailabilityAdjustment[] = []) {
  const adjustment = adjustments.find((item) => item.personId === personId && item.weekStart === weekStart);
  if (!adjustment) return { approvedLeaveDays: 0, unavailableDates: [] as string[], approvedLeave: false, reason: undefined as string | undefined };
  const unavailableDates = adjustment.unavailableDates || [];
  const approvedLeaveDays = adjustment.unavailableDays ?? unavailableDates.length;
  return {
    approvedLeaveDays,
    unavailableDates,
    approvedLeave: Boolean(adjustment.approvedLeave),
    reason: adjustment.reason,
  };
}

function getTaskWeekAllocation(task: Task, weekStart: string, weekEnd: string) {
  const bidDays = typeof task.bidHours === "number" && Number.isFinite(task.bidHours) ? task.bidHours : 0;
  if (bidDays <= 0) {
    return { allocatedDays: 0, usedScheduleAssumption: false };
  }

  const scheduleStart = task.plannedStartDate || task.createdAt?.slice(0, 10);
  const scheduleEnd = task.plannedEndDate || task.dueDate || task.plannedStartDate || task.updatedAt?.slice(0, 10) || scheduleStart;

  if (!scheduleStart || !scheduleEnd) {
    return { allocatedDays: bidDays, usedScheduleAssumption: true };
  }

  const taskWorkingDays = countWorkingDaysBetween(scheduleStart, scheduleEnd);
  const overlapStart = scheduleStart > weekStart ? scheduleStart : weekStart;
  const overlapEnd = scheduleEnd < weekEnd ? scheduleEnd : weekEnd;
  const overlapWorkingDays = countWorkingDaysBetween(overlapStart, overlapEnd);

  if (taskWorkingDays <= 0) {
    return { allocatedDays: bidDays, usedScheduleAssumption: true };
  }

  return {
    allocatedDays: (bidDays * overlapWorkingDays) / taskWorkingDays,
    usedScheduleAssumption: false,
  };
}

/**
 * Compliance problem: the artist is not logging enough time against their available working week.
 * Utilisation problem: we have not allocated enough work against their available working week.
 * These are different management signals, so we track both separately and only combine them in the
 * final status label for dashboard readability.
 */
export function computeArtistWeeklyFinanceSummary(
  person: Pick<Person, "id" | "displayName" | "fullName">,
  weekStartIso: string,
  context: ArtistWeeklyFinanceContext = {}
): ArtistWeeklyFinanceSummary {
  const minutesPerDay = context.minutesPerDay || DEFAULT_MINUTES_PER_DAY;
  const week = getWeekRange(weekStartIso);
  const availability = getAvailabilityForWeek(person.id, week.weekStart, context.availabilityAdjustments);
  const unavailableDateSet = new Set(availability.unavailableDates);
  const workingDays = Math.max(0, week.workingDates.filter((date) => !unavailableDateSet.has(date)).length - Math.max(0, availability.approvedLeaveDays - unavailableDateSet.size));

  const personTimeLogs = (context.timeLogs || []).filter(
    (timeLog) => timeLog.personId === person.id && timeLog.logDate >= week.weekStart && timeLog.logDate <= week.weekEnd
  );
  const loggedDays = personTimeLogs.reduce((sum, timeLog) => sum + (timeLog.minutes || 0), 0) / minutesPerDay;

  const assignedTasks = (context.tasks || []).filter((task) => (task.assignedArtistIds || []).includes(person.id));
  let allocatedDays = 0;
  let tasksWithScheduleAssumptions = 0;
  for (const task of assignedTasks) {
    const allocation = getTaskWeekAllocation(task, week.weekStart, week.weekEnd);
    allocatedDays += allocation.allocatedDays;
    if (allocation.usedScheduleAssumption) {
      tasksWithScheduleAssumptions += 1;
    }
  }

  const timelogComplianceScore = workingDays > 0 ? Math.round(clamp(loggedDays / workingDays, 0, 1.25) * 100) : 100;
  const allocationCoverageScore = workingDays > 0 ? Math.round(clamp(allocatedDays / workingDays, 0, 1.25) * 100) : 100;
  const loggedVsWorkingDaysVariance = loggedDays - workingDays;
  const allocatedVsWorkingDaysVariance = allocatedDays - workingDays;
  const loggedVsAllocatedDaysVariance = loggedDays - allocatedDays;
  const reasonCodes: string[] = [];

  if (availability.approvedLeave && availability.approvedLeaveDays > 0) {
    reasonCodes.push("approved_leave_adjustment");
  }
  if (tasksWithScheduleAssumptions > 0) {
    reasonCodes.push("scheduled_allocation_assumptions");
  }
  if (workingDays > 0 && loggedDays < workingDays * COMPLIANCE_TARGET_RATIO) {
    reasonCodes.push("timelog_compliance_gap");
  }
  if (workingDays > 0 && allocatedDays < workingDays * ALLOCATION_TARGET_RATIO) {
    reasonCodes.push("under_allocated_week");
  }
  if (allocatedDays > 0 && loggedDays > allocatedDays + 0.5) {
    reasonCodes.push("logged_more_than_allocated");
  }
  if (assignedTasks.length === 0 && personTimeLogs.length === 0) {
    reasonCodes.push("no_allocation_or_timelogs");
  }

  let confidenceScore = 100;
  if (tasksWithScheduleAssumptions > 0) confidenceScore -= 15;
  if (assignedTasks.length === 0 && personTimeLogs.length === 0) confidenceScore -= 25;
  if (availability.approvedLeave && availability.approvedLeaveDays > 0) confidenceScore -= 5;
  confidenceScore = clamp(confidenceScore, 5, 100);
  const confidence = scoreToConfidence(confidenceScore);

  let utilisationStatus: ArtistWeeklyFinanceSummary["utilisationStatus"] = "compliant_and_fully_allocated";
  if (availability.approvedLeave && availability.approvedLeaveDays >= 1 && workingDays === 0) {
    utilisationStatus = "unavailable_approved_leave";
  } else if (assignedTasks.length === 0 && personTimeLogs.length === 0) {
    utilisationStatus = "missing_data_uncertain";
  } else if (allocatedDays > 0 && loggedDays > allocatedDays + 0.5) {
    utilisationStatus = "over_logging_vs_allocation";
  } else if (workingDays > 0 && loggedDays < workingDays * COMPLIANCE_TARGET_RATIO) {
    utilisationStatus = "under_logging";
  } else if (workingDays > 0 && allocatedDays < workingDays * ALLOCATION_TARGET_RATIO) {
    utilisationStatus = "compliant_but_under_allocated";
  }

  const presentation = buildStatusSummary(utilisationStatus);

  return {
    personId: person.id,
    weekStart: week.weekStart,
    weekEnd: week.weekEnd,
    workingDays,
    loggedDays: roundDays(loggedDays),
    allocatedDays: roundDays(allocatedDays),
    loggedVsWorkingDaysVariance: roundDays(loggedVsWorkingDaysVariance),
    allocatedVsWorkingDaysVariance: roundDays(allocatedVsWorkingDaysVariance),
    loggedVsAllocatedDaysVariance: roundDays(loggedVsAllocatedDaysVariance),
    timelogComplianceScore,
    allocationCoverageScore,
    utilisationStatus,
    confidence,
    confidenceScore,
    assignedTaskCount: assignedTasks.length,
    tasksWithScheduleAssumptions,
    approvedLeaveDays: availability.approvedLeaveDays || undefined,
    reasonCodes,
    summaryLabel: presentation.summaryLabel,
    helperText: presentation.helperText,
    lastComputedAt: context.now || new Date().toISOString(),
  };
}

export function computeArtistWeeklyFinanceSummaries(
  people: Array<Pick<Person, "id" | "displayName" | "fullName">>,
  weekStartIso: string,
  context: ArtistWeeklyFinanceContext = {}
) {
  return people.reduce<Record<string, ArtistWeeklyFinanceSummary>>((acc, person) => {
    acc[person.id] = computeArtistWeeklyFinanceSummary(person, weekStartIso, context);
    return acc;
  }, {});
}

// TODO(finance-people): integrate approved leave / sickness / holiday data into availabilityAdjustments
// so working-day expectations become source-driven rather than default Monday-Friday assumptions.
// TODO(finance-people): add week/month forecasting views on top of these summaries rather than changing
// the artist-week output contract that current dashboard components already consume.
